import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import EliConversation from "components/ai/EliConversation";
import ConversationList from "./ConversationList";
import {
    doAgentChat,
    doLoadConversations,
    doOpenConversation,
    doNewChat,
    doRetryAgentChat,
    BUBBLE_AGENT_KEYS,
} from "../../../redux/copilot/copilotState";

// Eli files its turns under `eli`; the conversations that predate the router are
// still filed under `recruitment_partner` and are deliberately not rewritten,
// because they really were recruitment's. This page has to show both, exactly as
// the bubble does.
//
// Imported rather than redeclared: the bubble's history load filters by the same
// pair, and the whole-branch review found that the two surfaces silently
// disagreeing about it is what let the bubble restore a thread this sidebar
// cannot show.
const AGENT_KEY = BUBBLE_AGENT_KEYS;

export const EliChatPage = ({
    conversations,
    hasMore,
    offset,
    activeId,
    conversationsRequesting,
    conversationsError,
    responseData,
    requesting,
    loadConversations,
    openConversation,
    sendMessage,
    newChat,
    retry,
}) => {
    // The initial list.
    useEffect(() => {
        loadConversations({ agentKey: AGENT_KEY, offset: 0 });
    }, [loadConversations]);

    // Refresh on the FALLING edge of `requesting`, which is the moment a turn finishes.
    //
    // Not keyed on activeId, which is what this used to do and was wrong twice over:
    //
    //   1. activeId is set client-side by the doAgentChat reducer at the START of a
    //      turn, before the backend has persisted anything, so the refresh it triggered
    //      was guaranteed to return a list without the new thread. The reported symptom
    //      survived: send one message, sidebar still reads "No past conversations yet".
    //   2. doOpenConversationSuccess also sets conversationId, so merely OPENING a row
    //      re-fetched at offset 0, and the reducer REPLACES at offset 0. A user past 50
    //      threads who clicked Load older and then opened a row from page two watched
    //      the list collapse back to the newest 50, taking the row they were reading and
    //      the active highlight with it, and resetting offset 50 to 0.
    //
    // `requesting` goes false at every terminal action for a turn (doAgentChatSuccess,
    // doAgentChatFail, finishAgentStreaming, failAgentStreaming), so by then the thread
    // really does exist server-side. It is already mapped in for the thinking state, so
    // this costs no new wiring and no saga change.
    //
    // The offset === 0 guard is what keeps issue 2 fixed. Past page one the user is
    // browsing history, so leave their list alone rather than yanking it back to the
    // top. Opening a row now triggers nothing at all, which is correct: opening an
    // existing conversation creates nothing new to list.
    const turnWasRunning = useRef(requesting);
    useEffect(() => {
        const turnJustFinished = turnWasRunning.current && !requesting;
        turnWasRunning.current = requesting;
        if (turnJustFinished && offset === 0) {
            loadConversations({ agentKey: AGENT_KEY, offset: 0 });
        }
    }, [requesting, offset, loadConversations]);

    return (
        <div className="flex h-[calc(100vh_-_180px)] bg-white rounded-xl p-4">
            <ConversationList
                conversations={conversations}
                activeId={activeId}
                hasMore={hasMore}
                requesting={conversationsRequesting}
                error={conversationsError}
                onOpen={openConversation}
                onNewChat={newChat}
                onLoadOlder={() => loadConversations({ agentKey: AGENT_KEY, offset: offset + 50 })}
            />
            <div className="flex flex-col flex-1 min-w-0 pl-4">
                <EliConversation
                    responseData={responseData}
                    requesting={requesting}
                    onSend={(text) => sendMessage({ field: "chat", message: text })}
                    onRetry={retry}
                    messagesClassName="flex-1"
                />
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    conversations: state.copilot.conversationsData.results,
    hasMore: state.copilot.conversationsData.hasMore,
    offset: state.copilot.conversationsData.offset,
    conversationsRequesting: state.copilot.conversationsData.requesting,
    conversationsError: state.copilot.conversationsData.error,
    activeId: state.copilot.chatData.conversationId,
    responseData: state.copilot.chatData.results,
    requesting: state.copilot.chatData.requesting,
});

const mapDispatchToProps = (dispatch) => ({
    loadConversations: (params) => dispatch(doLoadConversations(params)),
    openConversation: (id) => dispatch(doOpenConversation(id)),
    sendMessage: (data) => dispatch(doAgentChat(data)),
    newChat: () => dispatch(doNewChat()),
    // TWO arguments, not one. doRetryAgentChat uses the RTK object form with a
    // prepare callback of (payload, errorId), and the reducer reads action.meta
    // .errorId to remove the right error bubble. Dropping errorId here would
    // remove the wrong bubble when two failed turns are on screen at once, which
    // is the exact cross-task defect the whole-branch review caught last time.
    // Copied verbatim from ChatEli.jsx's own mapDispatchToProps.
    retry: (data, errorId) => dispatch(doRetryAgentChat(data, errorId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EliChatPage);
